import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from "@mui/icons-material/Download";
import { Container, Box, CircularProgress, Snackbar, Alert } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import useSharedLink from "../../SharedLink";
import axios from "axios";
import { xorDecrypt } from "../../encrypt";
import axiosInstance from "../../api/axiosInstance";
import { useSnackbar } from "../../Providers/SnackbarProvider";
const InvoiceTable = () => {
  const sharedLink = useSharedLink();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true); // State to manage loading
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const { openSnackbar, dismiss } = useSnackbar();

  useEffect(() => {
    const fetchInvoices = async () => {
      const email = xorDecrypt(localStorage.getItem("username"));
      if (!email) {
        console.error("No email found in localStorage");
        return;
      }

      try {
        const response = await axiosInstance.get(`${sharedLink}/invoice`, {
          params: {
            email: email,
          },
          withCredentials: true,
        });
        if (response.status === 200) {
          // Ensure each invoice has a unique id
          const invoicesWithId = response.data.map((invoice, index) => ({
            id: invoice.invoiceId || index, // Use invoiceId if available, otherwise fallback to index
            ...invoice,
          }));
          setInvoices(invoicesWithId);
        }
      } catch (error) {
        console.error("Failed to fetch invoices:", error);
      } finally {
        setLoading(false); // Stop loading once data is fetched
      }
    };

    fetchInvoices();
  }, [sharedLink]);

  const handleDownload = async (invoiceId) => {
    openSnackbar("Downloading...", "info");
    try {
      const response = await axiosInstance.get(`${sharedLink}/download-invoice?invoiceId=${invoiceId}`,{
        responseType: "blob", 
        withCredentials: true,

      });
      
      if (response.status === 200) {
        const url = window.URL.createObjectURL(new Blob([response.data])); 
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `invoice-${invoiceId}.pdf`); // Dynamic filename
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        openSnackbar("Invoice downloaded successfully.", "success");
      } else {
        openSnackbar("Failed to download invoice.", "error");
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
      openSnackbar("An error occurred while downloading the invoice.", "error");
    }
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "2-digit", day: "2-digit" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const columns = [
    {
      field: "dateOfPurchase",
      headerName: "Date of Purchase",
      width: 350,
      valueFormatter: (params) => formatDate(params.value),
      headerAlign: "center",
      align: "center",
    },
    {
      field: "invoiceId",
      headerName: "Invoice ID",
      width: 550,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "download",
      headerName: "Download",
      width: 100,
      renderCell: (params) => (
        <IconButton onClick={() => handleDownload(params.row.invoiceId)}>
          <DownloadIcon style={{ color: "white" }} />
        </IconButton>
      ),
      headerAlign: "center",
      align: "center",
    },
  ];

  const theme = createTheme({
    components: {
      MuiDataGrid: {
        styleOverrides: {
          root: {
            color: "white",
            border: "1px solid #e9a132", // Entire grid border
            "& .MuiDataGrid-columnHeaders": {
              color: "white",
              backgroundColor: "#1c1c1c",
              borderBottom: "2px solid #e9a132", // Header border
            },
            "& .MuiDataGrid-cell": {
              color: "white",
              borderBottom: "1px solid #e9a132", // Cell border
            },
            "& .MuiDataGrid-iconSeparator": {
              color: "white", // three dots
              "&:hover": {
                color: "white", // three dots on hover
              },
            },
            "& .MuiDataGrid-sortIcon": {
              color: "white", // sorting arrows
            },
            "& .MuiDataGrid-menuIconButton": {
              color: "white", // menu button
            },
            "& .MuiSvgIcon-root": {
              color: "white", // all icons
            },
            "& .MuiDataGrid-footerContainer": {
              color: "white", // footer text
              backgroundColor: "#1c1c1c",
              borderTop: "2px solid #e9a132", // Footer border
            },
            "& .MuiTablePagination-root": {
              color: "white", // pagination text
            },
            "& .MuiTablePagination-actions": {
              color: "white", // pagination arrows
            },
            "& .MuiInputBase-root": {
              color: "white", // input text (e.g., rows per page)
            },
            "& .MuiSelect-icon": {
              color: "white", // dropdown arrow
            },
            // Custom scrollbar styles
            "& .MuiDataGrid-virtualScroller": {
              "&::-webkit-scrollbar": {
                width: "8px", // Width of the scrollbar
                height: "8px", // Height of the scrollbar
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#e9a132", // Scrollbar thumb color (orange)
                borderRadius: "8px", // Rounded corners for the scrollbar thumb
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#d0892f", // Darken the thumb on hover
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#333", // Scrollbar track color
                borderRadius: "8px", // Rounded corners for the scrollbar track
              },
            },
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <Container>
        <Box
          sx={{
            height: "80vh",
            width: "100%",
            display: "flex",
            marginTop: "50px",
            flexDirection: "column",
          }}
        >
          <h1
            style={{
              color: "white",
              alignSelf: "flex-start",
              marginBottom: "20px",
            }}
          >
            Invoices:
          </h1>
          {loading ? ( // Display loading spinner if data is still being fetched
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress sx={{ color: "#e9a132" }} />
            </Box>
          ) : (
            <DataGrid
              rows={invoices}
              columns={columns}
              getRowId={(row) => row.invoiceId}
              pageSize={5}
              sx={{ color: "white", border: "1px solid #e9a132" }}
            />
          )}
        </Box>
      </Container>
      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={6000}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default InvoiceTable;